import type { FullShipment } from '@fv/client-types'
import type { LandingPagePreference } from '@fv/models'

import { useRole } from './features/auth/hooks'
import { useDefaultWorkflow } from './features/profiile/hooks'
import type { RatesListingMode } from './features/quote/useRatesListing'
import { useUnifiedShipmentFlow } from './features/route-guides/hooks'
import { useUserSettings } from './hooks/settings'
import { type QuoteRouteParams, routes } from './routes'

export const useQuotePage = () => {
  const unifiedFlow = useUnifiedShipmentFlow()
  const defaultWorkflow = useDefaultWorkflow()

  if (unifiedFlow.isEnabled(defaultWorkflow)) return routes.create()

  return routes.quote(defaultWorkflow)
}

export const useHomePage = () => {
  const quotePage = useQuotePage()
  const role = useRole()
  const landingPage = useUserSettings().data?.preferences?.landingPage

  if (role === 'vendor') return quotePage

  const preferredRoute = getPreferredLandingPage({
    landingPage,
    quotePage,
  })
  if (preferredRoute) return preferredRoute

  return routes.dashboard()
}

function getPreferredLandingPage({
  landingPage,
  quotePage,
}: {
  landingPage?: LandingPagePreference
  quotePage: string
}) {
  switch (landingPage) {
    case 'quote-page':
      return quotePage
    case 'pending-shipments':
      return '/pending'
    case 'active-shipments':
      return routes.activeShipments()
    case 'analytics':
      return '/analytics'
    case 'auditing':
      return '/audit'
    default:
      return null
  }
}

export const useLoadEditRoute = () => {
  const unifiedFlow = useUnifiedShipmentFlow()

  return (
    load: Pick<FullShipment, 'loadId' | 'workflow' | 'quoteRequestId'>,
    mode: QuoteRouteParams['mode'],
  ) => {
    if (unifiedFlow.isEnabled(load.workflow)) {
      return mode === 'copy'
        ? routes.copyLoad(load.loadId)
        : routes.editLoad(load.loadId)
    }
    return routes.quote(load.workflow, {
      id: load.workflow === 'truckload' ? load.quoteRequestId : load.loadId,
      mode,
    })
  }
}

export const useLoadRatesPage = () => {
  const role = useRole()

  return (
    load: Pick<FullShipment, 'workflow' | 'loadId' | 'quoteRequestId'>,
    mode?: RatesListingMode,
  ) => {
    return routes.rates(load, mode, role)
  }
}
